/**
 * Oracle-specific database queries
 *
 * Oracle schemas are shown as "databases" in the explorer.
 * The adapter sets CURRENT_SCHEMA via ALTER SESSION before executing,
 * so queries can use SYS_CONTEXT('USERENV','CURRENT_SCHEMA') to filter.
 *
 * NOTE: All column aliases use quoted identifiers (e.g. AS "name") because
 * Oracle uppercases unquoted aliases. The service layer expects lowercase
 * property names (e.g. row.name, row.schema_name).
 */

/** Oracle system schemas to exclude from the explorer */
const ORACLE_SYSTEM_SCHEMAS = [
  'SYS', 'SYSTEM', 'DBSNMP', 'APPQOSSYS', 'CTXSYS', 'DVSYS', 'LBACSYS',
  'MDSYS', 'OLAPSYS', 'ORDDATA', 'ORDSYS', 'OUTLN', 'WMSYS', 'XDB',
  'GSMADMIN_INTERNAL', 'ANONYMOUS', 'APEX_PUBLIC_USER', 'AUDSYS',
  'DIP', 'DVF', 'FLOWS_FILES', 'GGSYS', 'GSMCATUSER', 'GSMUSER',
  'OJVMSYS', 'REMOTE_SCHEDULER_AGENT', 'SYS$UMF', 'SYSBACKUP',
  'SYSDG', 'SYSKM', 'SYSRAC', 'DBSFWUSER', 'DGPDB_INT',
  'ORACLE_OCM', 'SI_INFORMTN_SCHEMA', 'PDBADMIN', 'ORDS_PUBLIC_USER',
  'ORDS_METADATA',
].map(s => `'${s}'`).join(', ');

export const oracleQueries = {
  /** Get all schemas (shown as "databases" in the explorer) */
  databases: `
    SELECT USERNAME AS "name"
    FROM ALL_USERS
    WHERE USERNAME NOT IN (${ORACLE_SYSTEM_SCHEMAS})
    ORDER BY USERNAME
  `,

  /**
   * The schema a table gets created in: the session's CURRENT_SCHEMA (the
   * "database" the cascade selected), the same scope `tables` lists — so an
   * Oracle user with no tables yet is still offered as the import target.
   */
  schemas: `
    SELECT SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA') AS "name" FROM DUAL
  `,

  /** Get tables for the current schema */
  tables: `
    SELECT
      OWNER || '.' || TABLE_NAME AS "id",
      TABLE_NAME AS "name",
      OWNER AS "schema_name",
      'table' AS "object_type"
    FROM ALL_TABLES
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY TABLE_NAME
  `,

  /** Get views for the current schema */
  views: `
    SELECT
      OWNER || '.' || VIEW_NAME AS "id",
      VIEW_NAME AS "name",
      OWNER AS "schema_name",
      'view' AS "object_type"
    FROM ALL_VIEWS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY VIEW_NAME
  `,

  /** Get stored procedures for the current schema */
  storedProcedures: `
    SELECT
      OWNER || '.' || OBJECT_NAME AS "id",
      OBJECT_NAME AS "name",
      OWNER AS "schema_name",
      'stored-procedure' AS "object_type"
    FROM ALL_PROCEDURES
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
      AND OBJECT_TYPE = 'PROCEDURE'
    ORDER BY OBJECT_NAME
  `,

  /** Get functions for the current schema */
  functions: `
    SELECT
      OWNER || '.' || OBJECT_NAME AS "id",
      OBJECT_NAME AS "name",
      OWNER AS "schema_name",
      'function' AS "object_type"
    FROM ALL_PROCEDURES
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
      AND OBJECT_TYPE = 'FUNCTION'
    ORDER BY OBJECT_NAME
  `,

  /** Get packages for the current schema */
  packages: `
    SELECT
      OWNER || '.' || OBJECT_NAME AS "id",
      OBJECT_NAME AS "name",
      OWNER AS "schema_name",
      'package' AS "object_type"
    FROM ALL_OBJECTS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
      AND OBJECT_TYPE = 'PACKAGE'
    ORDER BY OBJECT_NAME
  `,

  /** Get sequences for the current schema */
  sequences: `
    SELECT
      SEQUENCE_OWNER || '.' || SEQUENCE_NAME AS "id",
      SEQUENCE_NAME AS "name",
      SEQUENCE_OWNER AS "schema_name",
      'sequence' AS "object_type"
    FROM ALL_SEQUENCES
    WHERE SEQUENCE_OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY SEQUENCE_NAME
  `,

  /** Get synonyms for the current schema */
  synonyms: `
    SELECT
      OWNER || '.' || SYNONYM_NAME AS "id",
      SYNONYM_NAME AS "name",
      OWNER AS "schema_name",
      'synonym' AS "object_type"
    FROM ALL_SYNONYMS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY SYNONYM_NAME
  `,

  /** Get database links for the current schema */
  dbLinks: `
    SELECT
      OWNER || '.' || DB_LINK AS "id",
      DB_LINK AS "name",
      OWNER AS "schema_name",
      'db-link' AS "object_type"
    FROM ALL_DB_LINKS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY DB_LINK
  `,

  /** Get materialized views for the current schema */
  materializedViews: `
    SELECT
      OWNER || '.' || MVIEW_NAME AS "id",
      MVIEW_NAME AS "name",
      OWNER AS "schema_name",
      'materialized-view' AS "object_type"
    FROM ALL_MVIEWS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY MVIEW_NAME
  `,

  /** Get types for the current schema */
  types: `
    SELECT
      OWNER || '.' || TYPE_NAME AS "id",
      TYPE_NAME AS "name",
      OWNER AS "schema_name",
      'type' AS "object_type"
    FROM ALL_TYPES
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY TYPE_NAME
  `,

  /** Get directories (global, not schema-specific) */
  directories: `
    SELECT
      OWNER || '.' || DIRECTORY_NAME AS "id",
      DIRECTORY_NAME AS "name",
      OWNER AS "schema_name",
      'directory' AS "object_type",
      DIRECTORY_PATH AS "directory_path"
    FROM ALL_DIRECTORIES
    ORDER BY DIRECTORY_NAME
  `,

  /** Get SQL property graphs for the current schema (Oracle 23ai+; ALL_PROPERTY_GRAPHS missing on older servers → ORA-00942 handled by the fetch config) */
  propertyGraphs: `
    SELECT
      OWNER || '.' || GRAPH_NAME AS "id",
      GRAPH_NAME AS "name",
      OWNER AS "schema_name",
      'property-graph' AS "object_type"
    FROM ALL_PROPERTY_GRAPHS
    WHERE OWNER = SYS_CONTEXT('USERENV', 'CURRENT_SCHEMA')
    ORDER BY GRAPH_NAME
  `,

  /**
   * Get columns for a table or view
   */
  columns: (schema: string, tableName: string) => `
    SELECT
      c.COLUMN_NAME AS "column_name",
      c.DATA_TYPE ||
        CASE
          WHEN c.DATA_TYPE IN ('VARCHAR2', 'NVARCHAR2', 'CHAR', 'NCHAR', 'RAW')
            THEN '(' || c.DATA_LENGTH || ')'
          WHEN c.DATA_TYPE = 'NUMBER' AND c.DATA_PRECISION IS NOT NULL
            THEN '(' || c.DATA_PRECISION ||
              CASE WHEN c.DATA_SCALE > 0 THEN ',' || c.DATA_SCALE ELSE '' END || ')'
          ELSE ''
        END AS "data_type",
      c.DATA_LENGTH AS "max_length",
      c.DATA_PRECISION AS "precision",
      c.DATA_SCALE AS "scale",
      CASE WHEN c.NULLABLE = 'Y' THEN 1 ELSE 0 END AS "is_nullable",
      CASE WHEN pk.COLUMN_NAME IS NOT NULL THEN 1 ELSE 0 END AS "is_primary_key",
      CASE WHEN c.IDENTITY_COLUMN = 'YES' THEN 1 ELSE 0 END AS "is_identity",
      c.DATA_DEFAULT AS "default_value",
      c.COLUMN_ID AS "ordinal_position"
    FROM ALL_TAB_COLUMNS c
    LEFT JOIN (
      SELECT cc.COLUMN_NAME
      FROM ALL_CONSTRAINTS con
      JOIN ALL_CONS_COLUMNS cc ON con.CONSTRAINT_NAME = cc.CONSTRAINT_NAME AND con.OWNER = cc.OWNER
      WHERE con.OWNER = '${schema.replace(/'/g, "''")}'
        AND con.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
        AND con.CONSTRAINT_TYPE = 'P'
    ) pk ON c.COLUMN_NAME = pk.COLUMN_NAME
    WHERE c.OWNER = '${schema.replace(/'/g, "''")}'
      AND c.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
    ORDER BY c.COLUMN_ID
  `,

  /**
   * Get table constraints
   */
  tableConstraints: (schema: string, tableName: string) => `
    SELECT
      c.CONSTRAINT_NAME AS "constraint_name",
      CASE c.CONSTRAINT_TYPE
        WHEN 'P' THEN 'PRIMARY KEY'
        WHEN 'R' THEN 'FOREIGN KEY'
        WHEN 'U' THEN 'UNIQUE'
        WHEN 'C' THEN 'CHECK'
        ELSE c.CONSTRAINT_TYPE
      END AS "constraint_type",
      cc.COLUMN_NAME AS "column_name",
      cc.POSITION AS "column_ordinal",
      r.OWNER AS "referenced_schema",
      r_cc.TABLE_NAME AS "referenced_table",
      r_cc.COLUMN_NAME AS "referenced_column",
      c.SEARCH_CONDITION AS "check_definition"
    FROM ALL_CONSTRAINTS c
    JOIN ALL_CONS_COLUMNS cc ON c.CONSTRAINT_NAME = cc.CONSTRAINT_NAME AND c.OWNER = cc.OWNER
    LEFT JOIN ALL_CONSTRAINTS r ON c.R_CONSTRAINT_NAME = r.CONSTRAINT_NAME AND c.R_OWNER = r.OWNER
    LEFT JOIN ALL_CONS_COLUMNS r_cc ON r.CONSTRAINT_NAME = r_cc.CONSTRAINT_NAME AND r.OWNER = r_cc.OWNER
      AND cc.POSITION = r_cc.POSITION
    WHERE c.OWNER = '${schema.replace(/'/g, "''")}'
      AND c.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
    ORDER BY
      c.CONSTRAINT_TYPE, c.CONSTRAINT_NAME, cc.POSITION
  `,

  /**
   * Get table indexes
   */
  tableIndexes: (schema: string, tableName: string) => `
    SELECT
      i.INDEX_NAME AS "index_name",
      ic.COLUMN_NAME AS "column_name",
      CASE WHEN i.UNIQUENESS = 'UNIQUE' THEN 1 ELSE 0 END AS "is_unique",
      i.INDEX_TYPE AS "index_type",
      ic.COLUMN_POSITION AS "key_ordinal"
    FROM ALL_INDEXES i
    JOIN ALL_IND_COLUMNS ic ON i.INDEX_NAME = ic.INDEX_NAME AND i.OWNER = ic.INDEX_OWNER
    WHERE i.TABLE_OWNER = '${schema.replace(/'/g, "''")}'
      AND i.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
      AND NOT EXISTS (
        SELECT 1 FROM ALL_CONSTRAINTS c
        WHERE c.CONSTRAINT_NAME = i.INDEX_NAME AND c.OWNER = i.OWNER AND c.CONSTRAINT_TYPE = 'P'
      )
    ORDER BY i.INDEX_NAME, ic.COLUMN_POSITION
  `,

  /**
   * Get default constraints (synthesized from column defaults)
   */
  tableDefaultConstraints: (schema: string, tableName: string) => `
    SELECT
      'DF_' || COLUMN_NAME AS "constraint_name",
      'DEFAULT' AS "constraint_type",
      COLUMN_NAME AS "column_name",
      DATA_DEFAULT AS "default_definition"
    FROM ALL_TAB_COLUMNS
    WHERE OWNER = '${schema.replace(/'/g, "''")}'
      AND TABLE_NAME = '${tableName.replace(/'/g, "''")}'
      AND DATA_DEFAULT IS NOT NULL
    ORDER BY COLUMN_NAME
  `,

  /**
   * Get table-level options + comment
   */
  tableOptions: (schema: string, tableName: string) => `
    SELECT
      t.TABLESPACE_NAME AS "tablespace_name",
      t.COMPRESSION AS "compression",
      t.LOGGING AS "logging",
      t.PCT_FREE AS "pct_free",
      t.PCT_USED AS "pct_used",
      t.INI_TRANS AS "ini_trans",
      t.IOT_TYPE AS "iot_type",
      tc.COMMENTS AS "comment"
    FROM ALL_TABLES t
    LEFT JOIN ALL_TAB_COMMENTS tc ON tc.OWNER = t.OWNER AND tc.TABLE_NAME = t.TABLE_NAME
    WHERE t.OWNER = '${schema.replace(/'/g, "''")}'
      AND t.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
  `,

  /**
   * Get column-level enrichments (computed expressions, comments, identity seed/increment)
   */
  columnEnrichments: (schema: string, tableName: string) => `
    SELECT
      c.COLUMN_NAME AS "column_name",
      c.VIRTUAL_COLUMN AS "virtual_column",
      c.DATA_DEFAULT AS "data_default",
      c.COLLATION AS "collation",
      cc.COMMENTS AS "comment",
      REGEXP_SUBSTR(ic.IDENTITY_OPTIONS, 'START WITH: ([0-9]+)', 1, 1, NULL, 1) AS "start_with",
      REGEXP_SUBSTR(ic.IDENTITY_OPTIONS, 'INCREMENT BY: ([0-9]+)', 1, 1, NULL, 1) AS "increment_by"
    FROM ALL_TAB_COLS c
    LEFT JOIN ALL_COL_COMMENTS cc
      ON cc.OWNER = c.OWNER AND cc.TABLE_NAME = c.TABLE_NAME AND cc.COLUMN_NAME = c.COLUMN_NAME
    LEFT JOIN ALL_TAB_IDENTITY_COLS ic
      ON ic.OWNER = c.OWNER AND ic.TABLE_NAME = c.TABLE_NAME AND ic.COLUMN_NAME = c.COLUMN_NAME
    WHERE c.OWNER = '${schema.replace(/'/g, "''")}'
      AND c.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
      AND c.HIDDEN_COLUMN = 'NO'
  `,

  /**
   * Get partition definition (definition-level only)
   */
  partition: (schema: string, tableName: string) => `
    SELECT
      pt.PARTITIONING_TYPE AS "partitioning_type",
      pt.SUBPARTITIONING_TYPE AS "subpartitioning_type",
      LISTAGG(pkc.COLUMN_NAME, ',') WITHIN GROUP (ORDER BY pkc.COLUMN_POSITION) AS "partition_columns"
    FROM ALL_PART_TABLES pt
    LEFT JOIN ALL_PART_KEY_COLUMNS pkc
      ON pkc.OWNER = pt.OWNER AND pkc.NAME = pt.TABLE_NAME
    WHERE pt.OWNER = '${schema.replace(/'/g, "''")}'
      AND pt.TABLE_NAME = '${tableName.replace(/'/g, "''")}'
    GROUP BY pt.PARTITIONING_TYPE, pt.SUBPARTITIONING_TYPE
  `,

  /**
   * Get table triggers
   */
  tableTriggers: (schema: string, tableName: string) => `
    SELECT
      TRIGGER_NAME AS "trigger_name",
      TRIGGER_TYPE AS "trigger_timing",
      TRIGGERING_EVENT AS "trigger_events",
      CASE WHEN STATUS = 'ENABLED' THEN 1 ELSE 0 END AS "is_enabled"
    FROM ALL_TRIGGERS
    WHERE TABLE_OWNER = '${schema.replace(/'/g, "''")}'
      AND TABLE_NAME = '${tableName.replace(/'/g, "''")}'
    ORDER BY TRIGGER_NAME
  `,

  /**
   * Get table statistics (Oracle doesn't have user-created statistics like MSSQL)
   */
  tableStatistics: (_schema: string, _tableName: string) => `
    SELECT
      NULL AS "statistic_name",
      NULL AS "column_name",
      NULL AS "column_ordinal",
      0 AS "auto_created",
      0 AS "user_created",
      NULL AS "last_updated"
    FROM DUAL WHERE 1 = 0
  `,

  /**
   * Get database (schema) properties
   */
  databaseProperties: (databaseName: string) => `
    SELECT
      u.USERNAME AS "database_name",
      u.DEFAULT_TABLESPACE AS "collation",
      u.USERNAME AS "owner",
      u.TEMPORARY_TABLESPACE AS "recovery_model",
      0 AS "compatibility_level"
    FROM ALL_USERS u
    WHERE u.USERNAME = '${databaseName.replace(/'/g, "''")}'
  `,

  /** Default database for Oracle (connected user's schema) */
  defaultDatabase: '',

  /** Fallback databases (none for Oracle) */
  fallbackDatabases: [] as string[],
};
